import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexResponsive,
    ApexTitleSubtitle,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from "ng-apexcharts";
import { Subscription } from "rxjs";
import { ToastService } from "../../../main/services";
import { SP_LOGIN_NEEDED, spendNotes } from "../../../v2/pages/year-over-year/year-over-year-view";
import { FX_FAILED_MESSAGE } from "../../../v2/shared/fx-warning/v2-fx-warning.component";
import { Tender } from "../../models";
import {
    TenderReport,
    TenderReportAvailableTender,
    TenderReportService,
} from "../../services/tender-report/tender-report.service";

export interface PreviousTenderOption extends Partial<TenderReportAvailableTender> {
    uid: string;
    name: string;
    kind: "auto" | "linked" | "tender";
}

export type BarChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    plotOptions: ApexPlotOptions;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis | ApexYAxis[];
    colors: string[];
    dataLabels: ApexDataLabels;
    legend: ApexLegend;
    tooltip: ApexTooltip;
    fill: ApexFill;
    title?: ApexTitleSubtitle;
};

export type DonutChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    colors: string[];
    legend: ApexLegend;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    responsive: ApexResponsive[];
    tooltip: ApexTooltip;
};

const PALETTE_CURRENT = "#0d6efd";
const PALETTE_PREVIOUS = "#94a3b8";
const PALETTE_GAIN = "#198754";
const PALETTE_LOSS = "#dc3545";

@Component({
    selector: "jft-tender-report",
    templateUrl: "./tender-report.component.html",
    styleUrls: ["./tender-report.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class TenderReportComponent implements OnInit, OnDestroy {
    public tender: Tender;
    public report: TenderReport | null = null;
    public loading = true;
    /** One wording for the exchange-rate outage in classic and v2 (decision #50). */
    public readonly fxFailedMessage = FX_FAILED_MESSAGE;

    public differentialChart: BarChartOptions | null = null;
    public donutCurrent: DonutChartOptions | null = null;
    public donutPrevious: DonutChartOptions | null = null;
    public netSupplierChart: BarChartOptions | null = null;
    public participationChart: BarChartOptions | null = null;

    // Dropdown state. null = nothing picked yet (initial/empty state).
    // 'auto' / 'linked' = synthetic sentinel options. Any other string = past tender uid.
    public selectedPreviousUid: string | null = null;
    public dropdownItems: PreviousTenderOption[] = [];

    private reportSub: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private service: TenderReportService,
        private toast: ToastService,
    ) {}

    public ngOnInit() {
        // Walk up via pathFromRoot because `reports` is a non-empty-path parent,
        // so resolved `tender` data doesn't auto-inherit into this child snapshot.
        this.tender = this.route.snapshot.pathFromRoot
            .map((r) => r.data["tender"])
            .find((t) => t);
        this.loadReport();
    }

    public ngOnDestroy() {
        this.reportSub?.unsubscribe();
    }

    public onPreviousTenderChange(uid: string | null) {
        this.selectedPreviousUid = uid;
        this.loadReport(uid || undefined);
    }

    private loadReport(previousTenderUid?: string) {
        this.loading = true;
        this.differentialChart = null;
        this.donutCurrent = null;
        this.donutPrevious = null;
        this.netSupplierChart = null;
        this.participationChart = null;

        // Cancel any in-flight request so a slower earlier response can't
        // clobber state we've set for the latest selection.
        this.reportSub?.unsubscribe();
        this.reportSub = this.service
            .fetch(this.tender.id, previousTenderUid)
            .subscribe(
                (report) => {
                    this.report = report;
                    this.dropdownItems = this.buildDropdownItems(report);
                    // Intentionally do NOT sync selectedPreviousUid from the server -
                    // the user's dropdown choice is authoritative.
                    if (report.has_previous) {
                        this.buildDifferentialChart();
                        this.buildSupplierDonuts();
                        this.buildNetSupplierChart();
                        // Participation funnel requires invited/bid counts, which only
                        // exist in 'linked' mode (virtual mode aggregates across many
                        // past tenders with heterogeneous invitation lists).
                        if (report.previous_mode === "linked") {
                            this.buildParticipationChart();
                        }
                    }
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;
                    const detail = error?.error?.detail;
                    this.toast.error(detail || "Failed to load tender report");
                },
            );
    }

    private buildDropdownItems(report: TenderReport): PreviousTenderOption[] {
        const items: PreviousTenderOption[] = [];
        if (report.has_auto_match_available) {
            items.push({
                uid: "auto",
                name: "Auto-match from past contracts",
                kind: "auto",
            });
        }
        if (report.has_linked_previous) {
            items.push({
                uid: "linked",
                name: "Linked previous tender",
                kind: "linked",
            });
        }
        for (const t of report.available_previous_tenders) {
            items.push({ ...t, kind: "tender" });
        }
        return items;
    }

    /** What the spend tile says instead of a figure ('' when there is one): the same words as v2. */
    public spendMissingText(side: "current" | "previous"): string {
        const snapshot = this.report?.[side];
        if (!snapshot || snapshot.kpis.estimated_spend != null) return "";
        return snapshot.spend_sp_withheld ? SP_LOGIN_NEEDED : "—";
    }

    /** Why part of the spend is missing (no analytics settings, awards not priced). */
    public spendNotes(): string[] {
        return this.report ? spendNotes(this.report) : [];
    }

    // Arrow reflects actual direction of change (up = value increased).
    // The `invert` flag only affects color/sentiment, never the arrow.
    public deltaIcon(delta: number | null | undefined): string {
        if (delta == null || delta === 0) return "fa-minus";
        return delta > 0 ? "fa-arrow-up" : "fa-arrow-down";
    }

    public deltaClass(
        delta: number | null | undefined,
        invert = false,
    ): string {
        if (delta == null || delta === 0) return "text-muted";
        const positive = delta > 0;
        const good = invert ? !positive : positive;
        return good ? "text-success" : "text-danger";
    }

    public formatDelta(
        value: number | null | undefined,
        fractionDigits: number,
    ): string {
        if (value == null) return "";
        const sign = value > 0 ? "+" : "";
        return `${sign}${value.toFixed(fractionDigits)}`;
    }

    // "1.2B", "45.3M", "892K", "450". Used for large currency amounts (spend movers).
    public formatCompact(n: number | null | undefined): string {
        if (n == null) return "-";
        const sign = n < 0 ? "-" : "";
        const abs = Math.abs(n);
        if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
        return `${sign}${Math.round(abs)}`;
    }

    public formatDeltaCompact(n: number | null | undefined): string {
        if (n == null) return "";
        const prefix = n > 0 ? "+" : n < 0 ? "-" : "";
        const abs = Math.abs(n);
        if (abs >= 1e9) return `${prefix}${(abs / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `${prefix}${(abs / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `${prefix}${(abs / 1e3).toFixed(1)}K`;
        return `${prefix}${Math.round(abs)}`;
    }

    // Render "18h", "2d 6h", "3d" from an hours count. Used by the bid-timing card.
    public formatHours(hours: number | null | undefined): string {
        if (hours == null) return "-";
        if (hours < 1) {
            const mins = Math.max(1, Math.round(hours * 60));
            return `${mins}m`;
        }
        if (hours < 24) return `${Math.round(hours)}h`;
        const days = Math.floor(hours / 24);
        const remH = Math.round(hours - days * 24);
        return remH ? `${days}d ${remH}h` : `${days}d`;
    }

    private buildDifferentialChart() {
        const cur = this.report.current.locations;
        const prev = new Map(
            this.report.previous.locations.map((l) => [l.location_id, l]),
        );

        const rows = cur
            .filter((l) => l.weighted_differential != null)
            .map((l) => {
                const previousLoc = prev.get(l.location_id);
                return {
                    label: l.iata || l.name || `#${l.location_id}`,
                    current: Number(l.weighted_differential),
                    previous: previousLoc?.weighted_differential ?? null,
                };
            })
            .filter((r) => r.previous != null)
            .sort(
                (a, b) =>
                    Math.abs(b.current - (b.previous ?? 0)) -
                    Math.abs(a.current - (a.previous ?? 0)),
            )
            .slice(0, 20);

        if (!rows.length) return;

        const categories = rows.map((r) => r.label);
        const seriesCurrent = rows.map((r) => +r.current.toFixed(4));
        const seriesPrevious = rows.map((r) => +(r.previous ?? 0).toFixed(4));
        const currency = this.report.currency;
        const units = this.report.units;

        this.differentialChart = {
            series: [
                { name: "This tender", data: seriesCurrent },
                { name: "Previous", data: seriesPrevious },
            ],
            chart: {
                type: "bar",
                height: Math.max(300, rows.length * 28 + 80),
                toolbar: { show: false },
                fontFamily: "inherit",
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: "65%",
                    borderRadius: 3,
                    dataLabels: { position: "top" },
                },
            },
            colors: [PALETTE_CURRENT, PALETTE_PREVIOUS],
            dataLabels: { enabled: false },
            xaxis: {
                categories,
                title: { text: `Differential (${currency} per ${units})` },
                labels: {
                    formatter: (val) => Number(val).toFixed(3),
                },
            },
            yaxis: { labels: { style: { fontSize: "11px" } } },
            legend: { position: "top", horizontalAlign: "left" },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (val) =>
                        `${Number(val).toFixed(4)} ${currency}/${units}`,
                },
            },
            fill: { opacity: 1 },
        };
    }

    private buildSupplierDonuts() {
        this.donutCurrent = this.makeDonut(this.report.current.by_supplier);
        this.donutPrevious = this.makeDonut(this.report.previous.by_supplier);
    }

    private makeDonut(
        suppliers: { name: string; awarded_volume: number }[],
    ): DonutChartOptions {
        const top = [...suppliers]
            .sort((a, b) => b.awarded_volume - a.awarded_volume)
            .slice(0, 8);
        const otherVol = suppliers
            .slice(8)
            .reduce((sum, s) => sum + s.awarded_volume, 0);
        const labels = top.map((s) => s.name);
        const series = top.map((s) => +s.awarded_volume.toFixed(2));
        if (otherVol > 0) {
            labels.push("Others");
            series.push(+otherVol.toFixed(2));
        }
        const palette = [
            "#0d6efd",
            "#198754",
            "#ffc107",
            "#dc3545",
            "#6610f2",
            "#0dcaf0",
            "#fd7e14",
            "#20c997",
            "#6c757d",
        ];
        const units = this.report.units;
        const formatNumber = (n: number) =>
            Math.round(n).toLocaleString("en-US");
        const formatCompact = (n: number) => {
            const abs = Math.abs(n);
            if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
            if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
            if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
            return formatNumber(n);
        };
        return {
            series,
            chart: { type: "donut", height: 300, fontFamily: "inherit" },
            labels,
            colors: palette,
            plotOptions: {
                pie: {
                    donut: {
                        size: "68%",
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#6c757d",
                                offsetY: -6,
                            },
                            value: {
                                show: true,
                                fontSize: "15px",
                                fontWeight: 700,
                                color: "#212529",
                                offsetY: 4,
                                formatter: (val: string) => {
                                    const n = Number(val);
                                    return Number.isFinite(n)
                                        ? formatCompact(n)
                                        : val;
                                },
                            },
                            total: {
                                show: true,
                                showAlways: true,
                                label: `Total (${units})`,
                                fontSize: "11px",
                                fontWeight: 500,
                                color: "#6c757d",
                                formatter: () =>
                                    formatCompact(
                                        series.reduce((a, b) => a + b, 0),
                                    ),
                            },
                        },
                    },
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) => `${val.toFixed(0)}%`,
                style: { fontSize: "11px", fontWeight: 600 },
                dropShadow: { enabled: false },
            },
            legend: { position: "bottom", fontSize: "11px" },
            tooltip: {
                y: {
                    formatter: (val) => `${formatNumber(Number(val))} ${units}`,
                },
            },
            responsive: [
                { breakpoint: 480, options: { chart: { height: 240 } } },
            ],
        };
    }

    private buildNetSupplierChart() {
        const prev = new Map(
            this.report.previous.by_supplier.map((s) => [s.id, s]),
        );
        const cur = new Map(
            this.report.current.by_supplier.map((s) => [s.id, s]),
        );
        const allIds = new Set<number>([...prev.keys(), ...cur.keys()]);

        const rows = Array.from(allIds).map((id) => {
            const c = cur.get(id);
            const p = prev.get(id);
            const name = c?.name || p?.name || `#${id}`;
            const change = (c?.awarded_volume ?? 0) - (p?.awarded_volume ?? 0);
            return { name, change };
        });

        const filtered = rows.filter((r) => Math.abs(r.change) > 0.01);
        if (!filtered.length) return;

        filtered.sort((a, b) => b.change - a.change);
        const top = filtered.slice(0, 15);

        const categories = top.map((r) => r.name);
        const series = top.map((r) => +r.change.toFixed(2));
        const colors = top.map((r) =>
            r.change >= 0 ? PALETTE_GAIN : PALETTE_LOSS,
        );

        const units = this.report.units;
        const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
        this.netSupplierChart = {
            series: [{ name: "Net volume change", data: series }],
            chart: {
                type: "bar",
                height: Math.max(280, top.length * 28 + 80),
                toolbar: { show: false },
                fontFamily: "inherit",
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: "65%",
                    borderRadius: 3,
                    distributed: true,
                },
            },
            colors,
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    val > 0 ? `+${fmt(val)}` : fmt(val),
                style: { fontSize: "11px", colors: ["#fff"] },
            },
            xaxis: {
                categories,
                title: { text: `Net volume change (${units})` },
                labels: { formatter: (val) => fmt(Number(val)) },
            },
            yaxis: { labels: { style: { fontSize: "11px" } } },
            legend: { show: false },
            tooltip: {
                y: {
                    formatter: (val) =>
                        `${Number(val) > 0 ? "+" : ""}${fmt(Number(val))} ${units}`,
                },
            },
            fill: { opacity: 1 },
        };
    }

    private buildParticipationChart() {
        const cur = this.report.current.participation;
        const prev = this.report.previous.participation;
        this.participationChart = {
            series: [
                {
                    name: "This tender",
                    data: [cur.invited, cur.bid, cur.awarded],
                },
                {
                    name: "Previous",
                    data: [prev.invited, prev.bid, prev.awarded],
                },
            ],
            chart: {
                type: "bar",
                height: 280,
                toolbar: { show: false },
                fontFamily: "inherit",
            },
            plotOptions: {
                bar: { horizontal: false, borderRadius: 3, columnWidth: "55%" },
            },
            colors: [PALETTE_CURRENT, PALETTE_PREVIOUS],
            dataLabels: {
                enabled: true,
                style: { fontSize: "11px" },
                offsetY: -18,
            },
            xaxis: { categories: ["Invited", "Submitted bid", "Awarded"] },
            yaxis: { title: { text: "Suppliers" } },
            legend: { position: "top", horizontalAlign: "left" },
            tooltip: { y: { formatter: (val) => `${val} suppliers` } },
            fill: { opacity: 1 },
        };
    }
}
